import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { WDSSelectionMode, WIZ_EVENT_TYPE, WIZ_PHYSIC_POSITION } from '@wizeoo/akm-core-lib';
import { WizComponentManager, WizHgroup, WizTileList, WizEventManager, WizTranslationManager, WizEvent, WizRoot, WizIcon, WizPopUp, PopUpManager } from '@wizeoo/akm-component-lib';
import { WidgetConfigurationItemObj} from '@wizeoo/akm-message-lib';

import { ALK_EVENT_TYPE, WIDGET_ACTION_ORIGIN, WIDGET_GLOBAL_KIND, WIDGET_KIND, WIDGET_STATE } from '../../../model/enums';
import { AlkEvent } from '../../../framework/events/alk.event';
import { FreedataWidgetBasic } from '../base/freedata-widget-basic';
import { IFreedataWidget, IWidget, IWidgetFilter } from '../../../manager/widgets/i-widget';
import { ViewData } from '../../../model/view.data';
import { PivotFilterExtendedRenderer } from '../pivot/renderers/filters/pivot-filter-extended.renderer';
import { TileConfig } from '../../multi-display/config/multi-display.config';
import { FreedataValueVO } from  '../../../vo/core/freedatavalue.vo';

import { FreedataTileSettingsPop } from './settings/freedata-tile-settings.pop';
import { FreedataTileRenderer } from './renderers/freedata-tile.renderer';
import { FastSelectData, WidgetEngine } from '../../../manager/widgets/widget.engine';
import { FreedataArgsVO } from '../../../vo/core/freedataargs.vo';
import { COMPUTE_MODE, FIELD_KIND, WizConstants } from '@wizeoo/akm-compute-lib';
import { FreedataTileFilterPop } from './settings/freedata-tile-filter.pop';
import { PopUtils } from '../../../framework/components/pop/pop-utils';
import { FeiVO } from '../../../vo/workingdata/fei.vo';


@Component({
  selector: 'freedata-tile-widget',
  templateUrl: './freedata-tile.widget.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
/** 
 * affichage en mode tuile
 * la pagination est gérée par le tile
 */
export class FreedataTileWidget extends FreedataWidgetBasic implements AfterViewInit, OnDestroy {

  constructor(elementRef: ElementRef, private _cdRef: ChangeDetectorRef) {
    super(elementRef);
    this.widgetKind = WIDGET_KIND.FD_TILE;
    this.widgetGlobalKind = WIDGET_GLOBAL_KIND.FREEDATA;
    this.settingsPopClassFactory = FreedataTileSettingsPop;
  }

  @ViewChild('containerFilter', { read: ViewContainerRef, static: true }) containerFilter: ViewContainerRef; // zone d'ajout des champs de filtre


  @ViewChild('tileList', { read: WizTileList, static: false }) tileList: WizTileList;
  @ViewChild('hgDetailZone', { read: WizHgroup, static: true }) hgDetailZone: WizHgroup; // zone de filtre, pour écouteur sur setFilter
  @ViewChild('moreFilterIcon') moreFilterIcon: WizIcon;
  /* ##############################################################################
                  composants spécifiques
  ############################################################################## */

  public filterComponents: ComponentRef<PivotFilterExtendedRenderer>[]; // les champs de filtre

  public renderer: any = FreedataTileRenderer;
  public dimensions: { cols: number, rows: number } = { cols: 200, rows: 100};// 1cell = 20px x 20px soit 1200px x 2000px

  public fakeFei: FeiVO = null; // fei Fake pour gestion des contrôles de cohérence



  /** configuration des tuiles */
  @Input()
  public tileConfig: TileConfig = null;

  /** passage des éléments de contexte au wizTile pour transmission aux renderers */
  public get fullContext(): any {
    return [this, this.context];
  }

  /** n'afficher que les éléments sélectionnés */
  public onlySelected: boolean = false;

  public get pageSize(): number {
    return 100;
  }

  private _indice: number = 0;
  public get indice(): number {
    if(this._indice > this.maxIndice) {
      this._indice = this.maxIndice;
    }
    
    return this._indice;
  }
  public set indice(value: number) {
    this._indice = value;
  }

  private get maxIndice(): number {
    let res: number = this.fdvDS?.getWorkedSource()?.length || 0;
    res = Math.floor(res/this.pageSize);
    // console.log('maxIndice', res);
    return res;
  }

  // public get showFirst(): boolean {
  //   return this.indice > 1;
  // }

  // public get showPrevious(): boolean {
  //   return this.indice > 0;
  // }

  // public get showNext(): boolean {
  //   return this.indice < this.maxIndice;
  // }

  // public get showLast(): boolean {
  //   return this.indice < this.maxIndice -1;
  // }

  public get showUnselect(): boolean {
    return this.showRowSelection === 'multiple' && this.fdvDS?.selectedItems?.length > 0;
  }

  public get showSelected(): boolean {
    return this.showRowSelection === 'multiple' && this.fdvDS?.selectedItems?.length > 0;
  }

  public get selectMode(): string  {
    if(this.onlySelected) {
      return '$.tile.showonly.all.$';
    }
    return '$.tile.showonly.selected.$';
  }

  /** passage des éléments de contexte au wizTile pour transmission aux renderers */
  // public get countLabel(): string {
  //   let mess: string = ''; 
  //   if(this.maxIndice > 0) {
  //     mess = WizTranslationManager.getInstance().translateKey('$.tile.page.$') +' ' + (this.indice + 1) +'/' + (this.maxIndice + 1);
  //   }
  //   return mess;
  // }

  /** passage des éléments de contexte au wizTile pour transmission aux renderers */
  public get countLabel(): string {
    let mess: string = ''; 
    if(this.fdvDS) {
      mess = this.fdvDS.getWorkedSource().length + ' / ' + this.fdvDS.getSource().length;
      mess += ' ' + WizTranslationManager.getInstance().translateKey('$.widget.elements.$');
    }

    return mess;
  }

   /* ##############################################################################
                    alimentation des données FEI / construction du grid
  ############################################################################### */
  
  /**
   * actions jouées sur le drawContent, le alterContent, ou en cas de modifs de filtre, de sélection
   * @override
   * @param origin WIDGET_ACTION_ORIGIN pour cibler qui demande
   * @param data l'objet éventuellement porté
   */
  public drawOrRefresh(origin: WIDGET_ACTION_ORIGIN, data?: any): void {
    // console.log('drawOrRefresh fdtile',  origin);
    let mustBuildRows: boolean = false; // on doit construire les lignes
    let mustRefresh: boolean = false; // création mise à jour de la DS de la liste
    let mustBuildFilters: boolean = false; // on doit construire les filtres
    let mustSelect: boolean = false; // on doit jouer la sélection de lignes

    // première création
    if(origin === WIDGET_ACTION_ORIGIN.INIT) {
      // alimentation du level pour filtre rapide des slaves
      this.freedataCode = this.context.fdk.split(ViewData.CONTEXT_SEPARATOR)[0];
      this.level = Number(this.context.fdk.split(ViewData.CONTEXT_SEPARATOR)[1]);
      
      // FEI pour les calculs
      let tmpFEI: FeiVO  = this.model.dataEngine.getFei(COMPUTE_MODE.LOCAL, 1, ViewData.DEFAULT_SCENARIO) as FeiVO;
      if(!tmpFEI) {
        tmpFEI = this.model.dataEngine.generateFei(this.model.currentVision, 
          this.model.currentVision.visionSteps[1], 
          ViewData.DEFAULT_SCENARIO, this.context, true);
      }
      if(!!tmpFEI) {
        this.model.measureEngine.buildFeiMatrix(tmpFEI);
        this.fakeFei = this.model.dataEngine.generateFakeFEI(tmpFEI) as FeiVO;  
      }

      // construction de la FeiKeyDef unique du widget
      this.buildFeiKeyDef(this.vision, this.context);
    }
    
    if(origin === WIDGET_ACTION_ORIGIN.READY) { // je me suis dit tout seul que j'étais prêt
      mustBuildFilters = true;
      mustBuildRows = true;
      mustSelect = true;
      mustRefresh = true;
    }

    // TODO sur configuration, capter les delta filter, colonnes, fields...

    // TODO TODO détecter une modification structurelle en cas 
    // contextDamaged
    // origine: changement de config
    // et mode pivot, sinon les lignes sont toujours OK
    if(origin === WIDGET_ACTION_ORIGIN.CONFIGURE) {   
      mustBuildFilters = true;
      mustBuildRows = true;
      mustRefresh = true;
    } 

    // modification du filtre... on filtre, et on corrige éventuellement la sélection
    // en cas de champ utilisé en colonne, on reconstruit le grid
    if(origin === WIDGET_ACTION_ORIGIN.FILTERING || origin === WIDGET_ACTION_ORIGIN.GLOBAL_FILTERING) {
      mustRefresh = true;
    }

    // modification du pool de sélection sur un master
    if(origin === WIDGET_ACTION_ORIGIN.OUTER_SELECT) {
      // console.log('outer select', this.widgetKind, this.fei.uniqueName, this.state);
      mustBuildRows = true;
      mustSelect = true;
      // this.fdvDS.refresh(); // on applique le filtre
      mustRefresh = true;
    }

    if(origin === WIDGET_ACTION_ORIGIN.RECOMPUTE) { // TODO TODO Faire moins bourrin
      mustBuildRows = this.syncDS(data); 
      mustRefresh = true;
    }
    if(mustBuildFilters) {
      this.buildFilters();
      this.buildFilterDrops();
    }
    if(mustBuildRows) {
      this.buildLines(); // délégué au WidgetBasic
      this.fdvDS.setSelectionMode(this.showRowSelection);
    }

    if(mustSelect) {
      this.feedSelectionFromContext(this._context); // alimentation de la sélection et passage à WIDGET_STATE = SELECTION_COMPLETE
    }

    if(mustRefresh) {
      // console.time('mustRefresh');
      this.fdvDS.refresh();

      //console.log('this.fdvDS', this.fdvDS.getWorkedSource())
      // console.timeEnd('mustRefresh');
      // console.time('buildTiles');
      this.buildTiles();
      // console.timeEnd('buildTiles');
    }
    this._cdRef.markForCheck();
  }

  /**
   * construction du pool de lignes, override pour check du mode superTile
   * @override
   */
  public async buildLines(): Promise<void> {
    //console.time('buildLines');
    if(!!this.context.fdk) {
     
      // par défaut, tous les FDVs du Freedata
      let basePool: FreedataValueVO[] = this.model.freedataEngine.getFreedataValuesByLevel(this.context.fdk);
      
      // réduction du périmètre par le master
      let master: IWidget = this.model.widgetEngine.checkFirstMaster(this);
      if(!!master) {
        basePool = this.model.widgetEngine.availableFreedataMasterLines(this, master as IFreedataWidget, this.context.fdk, basePool);
      }

      // construction du freeText si freeFilter
      if(this.hasGenericFilter()) {
        basePool.forEach(fdv => this.feedFdvAsString(fdv));  
      }

      // calcul des champs formules
      this.model.freedataEngine.computeFormulasOnFDV(this.freedataCode, basePool);


      if(!!this.superTile) {
        // console.log('superTile');
        let argCode: string = this.superTile.fdvf;
        let splittedPool: FreedataValueVO[] = [];
        let fake: FreedataValueVO = null;
        let raw: string;
        let parts: string[];

        let idx: number = 1;
        basePool.forEach(fdv => {
          raw = fdv.argsObjVO[argCode];
          if(!!raw) {
            parts= raw.split(WizConstants.KEY_SEPARATOR);
            parts.forEach(s => {
              fake = this.model.freedataEngine.cloneFDV(fdv);
              //fake.code = idx.toString();
              // idx++;
              fake.argsObjVO[argCode] = s;
              fake.byWidgetStringMap = fdv.byWidgetStringMap;
              splittedPool.push(fake);
            });
          }
        });
        this.fdvDS.setSource(splittedPool);
        //console.log('superTile', idx, this.fdvDS.getWorkedSource());
      } else {
        // console.log('superTile but base pool', this.fdvDS.getWorkedSource());
        this.fdvDS.setSource(basePool);
        // console.time('bob');
        // basePool.forEach(fdv => this.feedFdvAsString(fdv));  
        // console.timeEnd('bob');  
      }
    }
    //console.timeEnd('buildLines');
  }


  /**
   * construction 'initiale' des objets graphiques du widget :
   * - sur init véritable
   * - sur modification de la datasource
   * - sur modification de scenario (modifie la DS)
   */
  public drawContent(): void {
    
    let master: IWidget = this.model.widgetEngine.checkFirstMaster(this); // je cherche un master. S'il y en a un, READY n'est pas suffisant
    // si j'ai un master et qu'il n'est pas prêt, on ne fait rien
    if (!!master && master.state !== WIDGET_STATE.SELECTION_COMPLETE) { // j'ai un master, mais il n'est pas prêt
      // console.log('checkFeiState master not ready', this.widgetKind, this.fei.uniqueName);
      return;
    }

    if(this.state >= WIDGET_STATE.READY) { // déjà construit, plus rien a faire
      return;
    }

    this.drawOrRefresh(WIDGET_ACTION_ORIGIN.INIT); // actions communes draw / alter
    super.drawContent(); // pas au début, c'est lui qui dispatch les events
  }

  /**
   * reconstruction des objets graphiques du widget sur changement de structure : 
   * - modification des filtres qui impactent les colonnes
   * - changement de champs en ligne / colonnes / data
   * - changement de filtre
   * - changement de sélection
   * @param origin 
   */
  public alterContent(origin: WIDGET_ACTION_ORIGIN, data?: any): void {
    this.drawOrRefresh(origin, data);
    super.alterContent(origin, data);
  }

  /** retourne le wcio en mode multi-image */
  public get superTile(): WidgetConfigurationItemObj {
      // check mode superTile
      let _superTile: WidgetConfigurationItemObj = null;
      if(this.context?.ff?.length === 1) {
        let wcio: WidgetConfigurationItemObj = this.context?.ff[0];
        // console.log('wcio', wcio);
        let conf: FreedataArgsVO = this.model.freedataEngine.getArgFromFD(this.freedataCode, wcio.fdvf);
        if(conf?.dataType === FIELD_KIND.BINARY_MULTI) {
          _superTile = wcio;
        }
      }
      return _superTile;
  }

  private buildTiles(): void {

    let maxX: number = 0;
    let maxY: number = 0;
    
  
    if(!!this.context?.ff) {
      this.context.ff.forEach((item: WidgetConfigurationItemObj) => {
        const askedX: number = (item.x + item.cols)*23;
        const askedY: number = (item.y + item.rows)*23;
        if(!!askedX && askedX > maxX) {
          maxX = askedX;
        }
        if(!!askedY && askedY > maxY) {
          maxY = askedY;
        }
      });
      this.dimensions.cols = maxX;
      this.dimensions.rows = maxY;
    }

    // reconstruction des zones de filtre pour les grid et pivots (pas pour les incExc)
    // this.buildFilterDrops();

    super.drawContent();
    // on signale aux renderers
    WizEventManager.dispatchEvent(this, new AlkEvent(ALK_EVENT_TYPE.TILE_RENDERER_UPDATED, this.context));
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();
    WizEventManager.addListener(this, this.tileList, WIZ_EVENT_TYPE.SELECTION_CHANGED, this.innerSelectionChange);
    /** injection par programmation */
    WizEventManager.addListener(this, WizRoot.root(), ALK_EVENT_TYPE.PROGRAMMATIC_FEED, this.programmaticSelect);
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    WizEventManager.removeListener(this, this.tileList, WIZ_EVENT_TYPE.SELECTION_CHANGED);
    WizEventManager.removeListener(this, WizRoot.root(), ALK_EVENT_TYPE.PROGRAMMATIC_FEED);

  }

  /* ##############################################################################
                    gestion des filtres (composants drop)
     ############################################################################## */

  /**
   * interception de modification de filtre pour déclencher un refresh
   */
  protected innerUpdateFilter(e: AlkEvent): void {
    super.innerUpdateFilter(e);
    this.drawOrRefresh(WIDGET_ACTION_ORIGIN.FILTERING, e.data);
  }

  /** suppression des combos de filtres (avant changement de step, ou sur retour de pop) */
  private clearFilterDrops(): void {
    this.containerFilter.clear(); // vider le container des favoris
    this.filterComponents = []; // on rince la référence aux filtres
    this._needMore = false;
  }



  /** construction des dropdowns de filtre selon le contexte */
  protected buildFilterDrops() {
    this.clearFilterDrops(); // on rince tout
    
    let maxSize: number = this.hgDetailZone?.elementRef?.nativeElement?.offsetWidth;
    let currentSize: number = 0;

    // console.log('size', this.hgDetailZone.elementRef.nativeElement.offsetWidth);

    // construire unique le rendu, pas besoin d'alimenter les collections tant que la drop n'est pas ouverte
    if(this.filters) {
      [...this.filters.values()].forEach((filter: IWidgetFilter) => {
        let compoSize: number = WidgetEngine.getFilterSize(filter);
        if(currentSize + compoSize + 2 <= maxSize - WidgetEngine.ICONS_FILTER_SIZE) {
          const meCompo: ComponentRef<any> = WizComponentManager.instance.addChildTo(
            PivotFilterExtendedRenderer,
            this.containerFilter,
            { instanceProperties: { data: filter } }
          );
          this.filterComponents.push(meCompo);
          currentSize+= compoSize + 7;
        } else {
          this._needMore = true;
        }
        

      });
    }
    this._cdRef.markForCheck();
  }

  private _needMore: boolean = false;
  public get needMore(): boolean {
    return this._needMore;
  }

  public get hasFilter(): boolean {
    return this.filters?.size > 0;
  }

  public get hasActiveFilter(): boolean {
    let res: boolean = false;
    if(this.filters) {
      [...this.filters.values()].forEach((filter: IWidgetFilter) => {
       if(filter.active) {
        if(!!this.templatingMode || !filter.wcio.filtHide)
        res = true;
       }  
      });
    }
    return res;
  }

  /* ##############################################################################
                    gestion spécifique
     ############################################################################## */

    /**
   * FilterFunction du grid, sur la base des filtres
   * @override pour pagination
   * @param item WidgetLine
   * @returns true si visible
   */
  public fdvFilterFunction(item: FreedataValueVO): boolean {
    let result: boolean = super.fdvFilterFunction(item);
    
    if(!!this.onlySelected) { // on ne veut que les sélectionnés
      if(this.fdvDS.selectedItems.indexOf(item) < 0) {
        return false;
      }
    }
    // if(this.fdvDS.getWorkedSource().length > 100) {
    //   return false;
    // }

    return result;
  }

  /** navigation dans la vue */
  public doNav(way: string): void {
    switch(way) {
      case 'cancel':
        this.onlySelected = false;
        this.fdvDS.clearSelection();
        this.fdvDS.refresh();
        break;
      // case 'first':
      //   this.indice = 0;
      //   break;
      // case 'previous':
      //   this.indice = this.indice-1;
      //   break;
      // case 'next':
      //   this.indice = this.indice+1;
      //   break;
      // case 'last':
      //   this.indice = this.maxIndice;
      //   break;
      case 'selected':
        this.onlySelected = !this.onlySelected;
        this.fdvDS.refresh();
        break;       
    }
  }

  /** demande d'export depuis le bouton */
  public exportDatas(kind?: string): void {
    if(!kind || kind === 'excel') {
      // pas d'export Excel pour lui this.model.excelExportEngine.exportAsExcel('allkeyme', this);
    } else {
      this.model.excelExportEngine.exportAsPdf('allkeyme', this, this.elementRef, null, null);
    }
  }

  /** afficher les checkboxes de sélection */
  get showRowSelection(): WDSSelectionMode {
    if(!this._context?.shs) { // pas de sélection
      return 'none';
    } else {
      if(!!this._context?.shsu) { // sélection unique
        return 'single';
      } else {
        return 'multiple';
      }
    }
  }

  /**
   * changement de mon pool de sélection... intercepté sur AgGrid
   * on va informer les éventuels esclaves.
   * @override
   * @param e 
   */
  public innerSelectionChange(e: WizEvent): void {
    super.innerSelectionChange(e);

    let fsd: FastSelectData = {fei: this.fei, kind: this.widgetGlobalKind, data: this.fdvDS?.selectedItem};
    if(this.fdvDS.selectedItems?.length === 1 && fsd) {
      WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.PIVOT_DOUBLE, fsd));
    } else if(!this.fdvDS.selectedItems || this.fdvDS.selectedItems?.length === 0) {
      WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.UNSELECT, null));
    }
  }

  /* ##############################################################################
                    gestion tuiles sur champ unique binary_multi
     ############################################################################## */

  public clearAllFilter(): void {

    // ne pas rincer les filtres masqués
    if(this.filters) { // on rince les IWidgetFilter
      [...this.filters.values()].forEach((filter: IWidgetFilter) => {
        if(!!this.templatingMode || !filter.wcio.filtHide) {
          filter.values = null;
          filter.regex = null;
          filter.active = false;
        }
        
      });
    }
    
    // on rince le contexte
    this.context.f.forEach(wcio => {
      if(!!this.templatingMode || !wcio.filtHide) {
        wcio.sel = null;
      wcio.filtExp = null;
      }
    });

    // on rince les instances
    this.filterComponents.forEach(pfer => {
      if(!!this.templatingMode || !pfer.instance.context.filtHide) {
        pfer.instance.clearFilter(false); // clear sans dispatch
      }
      
    });

    // persistence uniquement hors du template, sinon, c'est délégué
    if (!this.templatingMode) { 
        this._saveContext();
    } else { // mise à jour du template, pas du contexte
        this.settings = this.context;
        WizEventManager.dispatchEvent(this, new AlkEvent(ALK_EVENT_TYPE.WIDGET_SET_SETTINGS, this));
    }     

    this.drawOrRefresh(WIDGET_ACTION_ORIGIN.FILTERING);// et application du filtre
  }
  
  /**
   * détection du resize pour redessiner les filtres
   * @override
   * @param width 
   * @param height 
   */
  public onResize(width, height): void {
    if(this._previousSize && this._previousSize.width !== width) {
      this.buildFilterDrops();
    }
    super.onResize(width, height);
  }

  /**
   * pop de liste des filtres
   */
  public moreFilterPop(): void {

    const pop: WizPopUp = new WizPopUp(
      FreedataTileFilterPop,
      false,
      false,
      WIZ_PHYSIC_POSITION.MIDDLE_CENTER,
      FreedataTileFilterPop.width,
      FreedataTileFilterPop.height,
    );

    const pos: DOMRect = this.moreFilterIcon.elementRef.nativeElement.getBoundingClientRect();
    const bestXY: [number, number] = PopUtils.setPopCoord(pos, pop, WIZ_PHYSIC_POSITION.BOTTOM_RIGHT);
    const x: string = bestXY[0] + 'px';
    const y: string = bestXY[1] + 'px';
    const targetPosition: any = { left: x, top: y }; // position physique
    pop.customPosition = targetPosition;
   
    pop.setCallback(this.backFromFilterPop.bind(this));

    pop.datas = [this,this.filters];

    if (PopUpManager.exist(pop)) { // suppression de l'instance existante
      pop.hide();
    }
    pop.show();
  }

  private backFromFilterPop(updates: Map<string, IWidgetFilter>): void {
 
    if(updates?.size > 0) { // on reconstruit les filtres
      
      // les contextes de filtre
      const filterMap: Map<string, WidgetConfigurationItemObj> = new Map(this.context.f.map((item) => [item.cod + item.fdvf, item]));

      [...updates.keys()].forEach(k => {
          let up: IWidgetFilter = updates.get(k);
          let existWcio = filterMap.get(k);
          if (up.active) { // seulement si le filtre est actif
            if(up.fkd?.datakind === FIELD_KIND.LIST) {
              existWcio.sel = up.values?.size > 0 ? [...up.values].join(ViewData.DOUBLEKEY_SEPARATOR) : null;
            } else {
              existWcio.sel = up.values?.size > 0 ? [...up.values].join(ViewData.KEY_SEPARATOR) : null;
            }
            existWcio.filtExp = up.regex;
            
          } else {
            existWcio.sel = null; // null car '' signifie qu'il y a quand même un filtre (=> filtre sur les valeurs vides)
            existWcio.filtExp = null;
          }
      });

      this.buildFilters();
      this.buildFilterDrops();
      
      // persistence uniquement hors du template, sinon, c'est délégué
      if (!this.templatingMode) { 
          this._saveContext();
      } else { // mise à jour du template, pas du contexte
          this.settings = this.context;
          WizEventManager.dispatchEvent(this, new AlkEvent(ALK_EVENT_TYPE.WIDGET_SET_SETTINGS, this));
      }  

      // et on filtre
      this.drawOrRefresh(WIDGET_ACTION_ORIGIN.FILTERING);// et application du filtre
    }
  }

  /**
   * selection par programmation
   */
  public programmaticSelect(e:AlkEvent): void {
    let data: FastSelectData = e.data;
    if(data.kind === WIDGET_GLOBAL_KIND.FREEDATA) {
      const fdv: FreedataValueVO = data.data as FreedataValueVO; 
      if(!!fdv && this.state >= WIDGET_STATE.READY) {
        const fdKey: string = fdv.freeDataCode + ViewData.CONTEXT_SEPARATOR + fdv.level;
        // console.log('fastSelect', this.context.fdk, fdKey, fdv);
        if(fdKey === this.context.fdk) { // vérifier que c'est le même FD et même Level
          const selectPool: FreedataValueVO[] = [];
         
          this.fdvDS.getWorkedSource().forEach((candidate: FreedataValueVO) => {
            if (fdv.code === candidate.code) {
              selectPool.push(fdv);
            }
          });
          this.fdvDS.select(selectPool);
          this.fdvDS.refresh();
          
          if(selectPool.length > 0) {
            let real: FreedataValueVO = this.tileList.wizDataProvider.getItemByKey(selectPool[0].code);
            // console.log('tile programmaticSelect', selectPool[0]);
            this.tileList.ensureIndexIsVisible(real);
          }
        }
      }
    } 
  }


}
